import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { ColumnFiltersState } from "@tanstack/react-table";
import type { SeriesLegendItemClickEventObject } from "highcharts/highcharts.src";
import moment from "moment";
import { Grid, GridItem } from "../../../../styled-system/jsx";
import Loading from "../../../components/core/blocks/Loading";
import DetailedMetric from "../../../components/core/blocks/DetailedMetric";
import MultiMetricChart from "../../../components/custom/merchandise/MultiMetricChart";
import useSelectedMetrics from "../../../components/custom/merchandise/hooks/useSelectedMetrics";
import { useDates } from "../../../dates/DatesContext";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import getMetricSpec from "../../merchandising/utils/metrics";
import { useGetSponsoredAdsAudienceByDate } from "../api/audience/useSponsoredAdsAudienceDatasetsQuery";
import { type SponsoredAdsAudienceRow } from "../../../zodios/apiDefinitions/api/sponsoredAdsAudience";
import { type SponsoredAdsAudienceAdType } from "./sponsoredAdsAudienceGroupings";

export function SponsoredAdsAudienceExpander({
  row,
  grouping,
  adType,
}: {
  row: SponsoredAdsAudienceRow;
  grouping: "segment_name" | "segment_name,campaign_id";
  adType: SponsoredAdsAudienceAdType;
}) {
  const acosOrRoas = useAcosOrRoas();
  const { group } = useDates();

  const filters: ColumnFiltersState = useMemo(() => {
    const result: ColumnFiltersState = [];
    if (row.segment_name) {
      result.push({ id: "segment_name", value: { op: "=", value: row.segment_name } });
    }
    if (grouping === "segment_name,campaign_id" && row.campaign_id != null) {
      result.push({ id: "campaign_id", value: { op: "=", value: row.campaign_id } });
    }
    return result;
  }, [row.segment_name, row.campaign_id, grouping]);

  const byDateGetter = useGetSponsoredAdsAudienceByDate(adType);
  const byDateQuery = useQuery({
    ...byDateGetter({ filters }),
    placeholderData: keepPreviousData,
  });

  const timeline = byDateQuery.data?.data ?? [];
  const dateFilterWithoutToday = timeline.filter((d) => moment().format("YYYY-MM-DD") !== d.start_date);

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["ad_sales", "ad_spend", acosOrRoas("acos", "roas")],
    "spAudienceExpander",
  );

  const metrics = useMemo(
    () => [
      getMetricSpec("ad_sales"),
      getMetricSpec("ad_spend"),
      acosOrRoas(getMetricSpec("acos"), getMetricSpec("roas")),
      getMetricSpec("clicks"),
      getMetricSpec("impressions"),
      getMetricSpec("ctr"),
    ],
    [acosOrRoas],
  );

  const handleLegendChange = (e: SeriesLegendItemClickEventObject) => {
    const id = e.target.options.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (byDateQuery.isLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }

  return (
    <div>
      <MultiMetricChart
        metrics={metrics}
        selectedMetrics={selectedMetrics}
        data={dateFilterWithoutToday}
        group={group}
        handleLegendChange={handleLegendChange}
        showAnnotations={false}
      />
      <Grid columns={6} gap={"medium"} paddingX={"medium"} paddingTop={"medium"}>
        {metrics.map((m) => (
          <GridItem key={m.id}>
            <DetailedMetric center metric={m} data={row} showTarget={false} />
          </GridItem>
        ))}
      </Grid>
    </div>
  );
}
